"use client";

import * as React from "react";
import { Text, SectionCard, CardTitle, CardDescription, Badge, Section, Button, DialogTitle, Dialog, DialogContent, DialogHeader, DialogFooter, DialogClose } from "@/src/components";

export default function DocsBlocksModal() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Modal
            </Text>
            <Badge variant="secondary">Block</Badge>
          </div>
          <Text as="p" className="text-lg text-muted-foreground max-w-2xl">
            A dialog component for focused interactions. Use for confirmations,
            forms, or important information that requires user attention.
          </Text>
        </div>
      </Section>

      {/* Basic Demo */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Basic Modal</CardTitle>
              <CardDescription>Simple modal with content</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <Button onClick={() => setIsOpen(true)}>Open Modal</Button>

          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Modal Title</DialogTitle>
              </DialogHeader>
              <Text as="p">
                Modal content goes here. This can include forms, information, or any other content
                that requires focused user attention.
              </Text>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
                <Button onClick={() => setIsOpen(false)}>Confirm</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </SectionCard>
      </Section>

      {/* Confirmation Modal */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Confirmation Modal</CardTitle>
              <CardDescription>For destructive or important actions</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <Button variant="outline" onClick={() => setConfirmOpen(true)}>
            Delete Item
          </Button>

          <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Item</DialogTitle>
              </DialogHeader>
              <Text as="p" className="text-muted-foreground">
                Are you sure you want to delete this item? This action cannot be undone.
              </Text>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
                <Button
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => setConfirmOpen(false)}
                >
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </SectionCard>
      </Section>

      {/* Usage */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Usage</CardTitle>
              <CardDescription>Basic modal implementation</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
            <pre>{`import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/src/components";
import { Button } from "@/src/components";

const [open, setOpen] = useState(false);

<Button onClick={() => setOpen(true)}>Open Modal</Button>

<Dialog open={open} onOpenChange={setOpen}>
  <DialogContent>
    <DialogHeader>
      <DialogTitle>Confirm Action</DialogTitle>
    </DialogHeader>
    <p className="text-muted-foreground">
      Are you sure you want to proceed?
    </p>
    <DialogFooter>
      <Button variant="outline" onClick={() => setOpen(false)}>
        Cancel
      </Button>
      <Button onClick={handleConfirm}>Confirm</Button>
    </DialogFooter>
  </DialogContent>
</Dialog>`}</pre>
          </div>
        </SectionCard>
      </Section>

      {/* Import */}
      <Section>
        <SectionCard
          header={<CardTitle>Import</CardTitle>}
          contentClassName="p-6"
        >
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <code>{`import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/src/components";`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Props */}
      <Section>
        <SectionCard
          header={<CardTitle>Props</CardTitle>}
          contentClassName="p-6"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-semibold">Component</th>
                <th className="text-left py-2 font-semibold">Prop</th>
                <th className="text-left py-2 font-semibold">Type</th>
                <th className="text-left py-2 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">Dialog</td>
                <td className="py-2 font-mono text-xs">open</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 text-muted-foreground">Controlled open state</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">Dialog</td>
                <td className="py-2 font-mono text-xs">onOpenChange</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">(open: boolean) =&gt; void</td>
                <td className="py-2 text-muted-foreground">Called when open state changes</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">DialogContent</td>
                <td className="py-2 font-mono text-xs">showCloseButton</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 text-muted-foreground">Show X button in header (default: true)</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">DialogTitle</td>
                <td className="py-2 font-mono text-xs">children</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
                <td className="py-2 text-muted-foreground">Title text content</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">DialogHeader</td>
                <td className="py-2 font-mono text-xs">children</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
                <td className="py-2 text-muted-foreground">Header content</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">DialogFooter</td>
                <td className="py-2 font-mono text-xs">children</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
                <td className="py-2 text-muted-foreground">Footer content (typically actions)</td>
              </tr>
            </tbody>
          </table>
        </SectionCard>
      </Section>

      {/* Best Practices */}
      <Section>
        <SectionCard
          header={<CardTitle>Best Practices</CardTitle>}
          contentClassName="p-6 space-y-4"
        >
          <ul className="space-y-2 text-sm">
            <li className="flex gap-2">
              <div className="text-primary">-</div>
              Use modals sparingly - they interrupt user flow
            </li>
            <li className="flex gap-2">
              <div className="text-primary">-</div>
              Always provide a clear way to close (Cancel button or click outside)
            </li>
            <li className="flex gap-2">
              <div className="text-primary">-</div>
              Keep modal content focused on a single task
            </li>
            <li className="flex gap-2">
              <div className="text-primary">-</div>
              Use descriptive titles that explain the purpose
            </li>
            <li className="flex gap-2">
              <div className="text-primary">-</div>
              For destructive actions, use a confirmation modal with clear warning
            </li>
          </ul>
        </SectionCard>
      </Section>
    </>
  );
}
